import * as styles from "./ClientQuickview.css";

import { useAtomValue } from "jotai";
import type { JSX, ReactNode } from "react";
import type { RouteName } from "routing";

import { Avatar } from "@frontend/components/atoms/Avatar";
import { Button } from "@frontend/components/atoms/Button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerPopup,
  DrawerTitle,
} from "@frontend/components/atoms/Drawer";
import { Icon } from "@frontend/components/atoms/Icon";
import { Skeleton } from "@frontend/components/atoms/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { ContactPreviewPopover } from "@frontend/features/addressbook";
import { url } from "@frontend/lib/http";
import { Trans, useLingui } from "@lingui/react/macro";

import { clientQuickviewAtom } from "../stores/quickview";
import type {
  ClientQuickview as Quickview,
  QuickviewPerson,
  QuickviewProject,
  TypedAddress,
} from "../types";

export type ClientQuickviewProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (clientId: number) => void;
  onArchive: (clientId: number) => void;
};

/**
 * The drawer a row opens, the React counterpart of
 * `Default/quickview.html.twig`: enough of a client to decide whether to open
 * it. It stays non-modal, so the list behind it keeps working and moving from
 * row to row just refills the panel.
 */
export function ClientQuickview({
  open,
  onOpenChange,
  onEdit,
  onArchive,
}: ClientQuickviewProps): JSX.Element {
  const { t } = useLingui();

  const { client, loading, error } = useAtomValue(clientQuickviewAtom);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      swipeDirection="right"
      modal={false}
      disablePointerDismissal
    >
      <DrawerPopup size="lg" border="glass" backdrop={false}>
        <DrawerHeader>
          <DrawerTitle className={styles.title}>
            {client ? (
              <>
                <Avatar
                  size="md"
                  url={client.avatarUrl ?? undefined}
                  fallback={<Icon name="Briefcase" size="sm" />}
                />

                <span className={styles.identity}>
                  <span className={styles.id}>#{client.id}</span>
                  <span className={styles.name}>{client.displayName}</span>
                </span>
              </>
            ) : (
              <Trans>Client</Trans>
            )}
          </DrawerTitle>

          <div className={styles.actions}>
            {client ? (
              <>
                {client.isArchived ? null : (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="md"
                          aria-label={t`Archive the client`}
                          onClick={() => onArchive(client.id)}
                        >
                          <Icon name="Archive" size="sm" />
                        </Button>
                      }
                    />

                    <TooltipContent side="bottom">
                      {t`Archive the client`}
                    </TooltipContent>
                  </Tooltip>
                )}

                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="md"
                        aria-label={t`Edit the client`}
                        onClick={() => onEdit(client.id)}
                      >
                        <Icon name="Pencil" size="sm" />
                      </Button>
                    }
                  />

                  <TooltipContent side="bottom">
                    {t`Edit the client`}
                  </TooltipContent>
                </Tooltip>
              </>
            ) : null}

            <DrawerClose />
          </div>
        </DrawerHeader>

        <DrawerContent className={styles.content}>
          {error ? (
            <p className={styles.alert} role="alert">
              {error}
            </p>
          ) : null}

          {null === client ? (
            loading ? (
              <Skeleton className={styles.placeholder} />
            ) : null
          ) : (
            <Body client={client} loading={loading} />
          )}
        </DrawerContent>
      </DrawerPopup>
    </Drawer>
  );
}

function Body({
  client,
  loading,
}: {
  client: Quickview;
  loading: boolean;
}): JSX.Element {
  const { t } = useLingui();

  const otherAddresses = client.otherAddressCount;

  const reachable =
    0 < client.phones.length ||
    0 < client.emails.length ||
    0 < client.addresses.length;

  return (
    <div className={styles.body} data-loading={loading || undefined}>
      <section className={styles.section}>
        {client.statusLabel ? (
          <Field label={t`Status`}>{client.statusLabel}</Field>
        ) : null}

        {client.typeLabel ? (
          <Field label={t`Kind`}>{client.typeLabel}</Field>
        ) : null}

        {client.fieldLabel ? (
          <Field label={t`Line of business`}>{client.fieldLabel}</Field>
        ) : null}

        {client.regionLabel ? (
          <Field label={t`Region`}>{client.regionLabel}</Field>
        ) : null}

        {client.provenanceLabel ? (
          <Field label={t`Brought in by`}>{client.provenanceLabel}</Field>
        ) : null}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <Trans>Contact details</Trans>
        </h3>

        {client.phones.map((phone, index) => (
          <Field key={`phone-${index}`} label={phone.place}>
            {phone.number}
          </Field>
        ))}

        {client.emails.map((email, index) => (
          <Field key={`email-${index}`} label={email.place}>
            <a href={`mailto:${email.address}`}>{email.address}</a>
          </Field>
        ))}

        {client.urls.map((link, index) => (
          <Field key={`url-${index}`} label={link.place}>
            <a href={link.url} target="_blank" rel="noopener">
              {link.url}
            </a>
          </Field>
        ))}

        {client.addresses.map((address, index) => (
          <Field key={`address-${index}`} label={address.place ?? ""}>
            <Address address={address} />
          </Field>
        ))}

        {0 < otherAddresses ? (
          <p className={styles.empty}>
            <Trans>{otherAddresses} more addresses on file</Trans>
          </p>
        ) : null}

        {reachable ? null : (
          <p className={styles.empty}>
            <Trans>Nothing to reach this client on.</Trans>
          </p>
        )}
      </section>

      <People
        title={t`In charge`}
        people={client.accountants}
        empty={t`Nobody follows this client yet.`}
        previewLabel={t`See the details`}
      />

      {0 === client.contacts.length ? null : (
        <People
          title={t`Contacts`}
          people={client.contacts}
          empty=""
          previewLabel={t`See the details`}
        />
      )}

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <Trans>Projects</Trans>
        </h3>

        {0 === client.subscriptions.length &&
        0 === client.interventions.length ? (
          <p className={styles.empty}>
            <Trans>This client has no project yet.</Trans>
          </p>
        ) : (
          <>
            {0 === client.subscriptions.length ? null : (
              <Field label={t`Subscriptions`}>
                <Projects
                  projects={client.subscriptions}
                  route="eco_pro_project_subscription_index"
                />
              </Field>
            )}

            {0 === client.interventions.length ? null : (
              <Field label={t`Interventions`}>
                <Projects
                  projects={client.interventions}
                  route="eco_pro_project_intervention_index"
                />
              </Field>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function Projects({
  projects,
  route,
}: {
  projects: QuickviewProject[];
  route: RouteName;
}): JSX.Element {
  return (
    <div className={styles.projects}>
      {projects.map((project) => (
        <a
          key={project.id}
          className={project.isArchived ? styles.archived : undefined}
          href={url(route, { path: project.id })}
        >
          {project.label} - {project.id}
        </a>
      ))}
    </div>
  );
}

function People({
  title,
  people,
  empty,
  previewLabel,
}: {
  title: string;
  people: QuickviewPerson[];
  empty: string;
  previewLabel: string;
}): JSX.Element {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>{title}</h3>

      {0 === people.length ? (
        <p className={styles.empty}>{empty}</p>
      ) : (
        people.map((person, index) => (
          <Field key={index} label={person.partLabel ?? ""}>
            <span className={styles.person}>
              <span>{person.personLabel ?? "-"}</span>

              {person.contactId ? (
                <ContactPreviewPopover
                  contactId={person.contactId}
                  side="left"
                  label={previewLabel}
                />
              ) : null}
            </span>
          </Field>
        ))
      )}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={styles.field}>
      <div className={styles.label}>{label}</div>
      <div className={styles.value}>{children}</div>
    </div>
  );
}

function Address({ address }: { address: TypedAddress }): JSX.Element {
  const second = [address.extendedAddress, address.postOfficeBox]
    .filter(Boolean)
    .join(" ");

  const region = [address.state, address.country].filter(Boolean).join(" ");

  return (
    <div className={styles.address}>
      <div>{address.street}</div>

      {second ? <div>{second}</div> : null}

      <div>
        {address.postalCode} {address.city}
        {region ? ` / ${region}` : null}
      </div>
    </div>
  );
}
